/**
 * Etiquetas de gondola: codigo de barras + nombre del articulo.
 *
 * Es la etiqueta tipica de bazar, la que se pega en el estante: solo el codigo
 * escaneable y el nombre. No lleva precio, para que un cambio de lista no
 * obligue a reimprimir toda la gondola.
 *
 * El codigo se dibuja en Code 128 (subconjunto B), que es el estandar para SKU
 * alfanumericos y lo que leen los lectores de mostrador.
 */

import jsPDF from 'jspdf';

/* ------------------------------------------------------------------ Code 128 */

/**
 * Anchos de barra de los 107 simbolos de Code 128.
 * Cada string son 6 digitos: barra, espacio, barra, espacio, barra, espacio,
 * en modulos. Los primeros 103 son los datos, despues Start A/B/C y Stop.
 */
const CODE128_PATTERNS = [
    '212222', '222122', '222221', '121223', '121322', '131222', '122213', '122312',
    '132212', '221213', '221312', '231212', '112232', '122132', '122231', '113222',
    '123122', '123221', '223211', '221132', '221231', '213212', '223112', '312131',
    '311222', '321122', '321221', '312212', '322112', '322211', '212123', '212321',
    '232121', '111323', '131123', '131321', '112313', '132113', '132311', '211313',
    '231113', '231311', '112133', '112331', '132131', '113123', '113321', '133121',
    '313121', '211331', '231131', '213113', '213311', '213131', '311123', '311321',
    '331121', '312113', '312311', '332111', '314111', '221411', '431111', '111224',
    '111422', '121124', '121421', '141122', '141221', '112214', '112412', '122114',
    '122411', '142112', '142211', '241211', '221114', '413111', '241112', '134111',
    '111242', '121142', '121241', '114212', '124112', '124211', '411212', '421112',
    '421211', '212141', '214121', '412121', '111143', '111341', '131141', '114113',
    '114311', '411113', '411311', '113141', '114131', '311141', '411131', '211412',
    '211214', '211232', '2331112',
];

/** Start B = 104, Stop = 106 (el patron de Stop lleva una barra extra). */
const CODE128_START_B = 104;
const CODE128_STOP = 106;

/**
 * Convierte el texto a la secuencia de simbolos de Code 128 B.
 *
 * En el subconjunto B cada caracter imprimible ASCII (32..126) vale
 * `code - 32`. Se descarta lo que quede fuera de ese rango para no generar un
 * codigo que despues el lector no pueda interpretar.
 */
const code128Values = (text: string): number[] => {
    const values: number[] = [CODE128_START_B];
    for (const char of text) {
        const code = char.charCodeAt(0);
        if (code < 32 || code > 126) continue;
        values.push(code - 32);
    }
    // Digito de control: suma ponderada por la posicion, modulo 103.
    let sum = CODE128_START_B;
    values.slice(1).forEach((value, i) => { sum += value * (i + 1); });
    values.push(sum % 103);
    values.push(CODE128_STOP);
    return values;
};

/**
 * Dibuja el codigo de barras centrado en `x` con el ancho maximo indicado.
 * Devuelve el ancho real usado, que puede ser menor si el codigo es corto.
 */
const drawCode128 = (
    doc: jsPDF,
    text: string,
    centerX: number,
    y: number,
    maxWidth: number,
    height: number,
): number => {
    const values = code128Values(text);
    const patterns = values.map(v => CODE128_PATTERNS[v]);

    const totalModules = patterns.reduce(
        (acc, pattern) => acc + pattern.split('').reduce((s, d) => s + Number(d), 0),
        0,
    );
    if (totalModules === 0) return 0;

    // El modulo se acota para que un codigo corto no salga con barras enormes.
    const unit = Math.min(maxWidth / totalModules, 0.42);
    const width = totalModules * unit;
    let cursor = centerX - width / 2;

    doc.setFillColor(0, 0, 0);
    patterns.forEach(pattern => {
        // Las posiciones pares son barra; las impares, espacio.
        pattern.split('').forEach((digit, i) => {
            const w = Number(digit) * unit;
            if (i % 2 === 0) doc.rect(cursor, y, w, height, 'F');
            cursor += w;
        });
    });

    return width;
};

/* ------------------------------------------------------------------ etiqueta */

export interface EtiquetaArticulo {
    /** Codigo que se imprime y se escanea. */
    codigo: string;
    /** Nombre visible del articulo. */
    nombre: string;
}

/** Medidas de la etiqueta y de la hoja, en milimetros. */
const LABEL = {
    width: 50,
    height: 30,
    marginX: 8,
    marginY: 10,
    gapX: 4,
    gapY: 4,
};

/**
 * Genera una hoja A4 con las etiquetas en grilla y abre el dialogo de
 * impresion. `copias` repite cada articulo (una gondola suele necesitar
 * varias etiquetas del mismo producto).
 */
export const generarEtiquetasPDF = (articulos: EtiquetaArticulo[], copias = 1) => {
    const items: EtiquetaArticulo[] = [];
    articulos.forEach(a => {
        for (let i = 0; i < Math.max(1, copias); i++) items.push(a);
    });
    if (items.length === 0) return;

    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    const perRow = Math.max(
        1,
        Math.floor((pageW - LABEL.marginX * 2 + LABEL.gapX) / (LABEL.width + LABEL.gapX)),
    );
    const perCol = Math.max(
        1,
        Math.floor((pageH - LABEL.marginY * 2 + LABEL.gapY) / (LABEL.height + LABEL.gapY)),
    );
    const perPage = perRow * perCol;

    items.forEach((item, index) => {
        if (index > 0 && index % perPage === 0) doc.addPage();

        const slot = index % perPage;
        const col = slot % perRow;
        const row = Math.floor(slot / perRow);
        const x = LABEL.marginX + col * (LABEL.width + LABEL.gapX);
        const y = LABEL.marginY + row * (LABEL.height + LABEL.gapY);
        const centerX = x + LABEL.width / 2;

        // Recuadro tenue: guia de corte, no debe competir con las barras.
        doc.setDrawColor(210, 210, 210);
        doc.setLineWidth(0.2);
        doc.rect(x, y, LABEL.width, LABEL.height);

        // Nombre arriba, hasta dos lineas; si no entra, se recorta con puntos.
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        const lines = doc.splitTextToSize(item.nombre || '', LABEL.width - 6).slice(0, 2);
        lines.forEach((line: string, i: number) => {
            doc.text(line, centerX, y + 5 + i * 3.6, { align: 'center' });
        });

        const barcodeTop = y + 5 + lines.length * 3.6 + 1.5;
        const barcodeHeight = Math.max(8, y + LABEL.height - 6 - barcodeTop);
        drawCode128(doc, item.codigo, centerX, barcodeTop, LABEL.width - 6, barcodeHeight);

        // El codigo en texto, para poder tipearlo si el lector falla.
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.text(item.codigo, centerX, y + LABEL.height - 2, { align: 'center' });
    });

    doc.autoPrint();
    const url = doc.output('bloburl');
    if (url) window.open(url, '_blank');
};
